import { Copy, Check, Loader2, Clock } from 'lucide-react';
import { InAppBrowserName } from '@/lib/in-app-browser';

interface IOSInstructionsProps {
  browserName?: InAppBrowserName | null;
  onCopyLink: () => void;
  copied: boolean;
  copyError?: boolean;
  isLoading?: boolean;
  countdown: number | null;
  formatCountdown: (seconds: number) => string;
  tokenFallbackUsed?: boolean;
}

export function IOSInstructions({
  onCopyLink,
  copied,
  isLoading,
  countdown,
  formatCountdown,
  tokenFallbackUsed,
}: IOSInstructionsProps) {
  const hasActiveToken = countdown !== null && countdown > 0;

  return (
    <div className="w-full max-w-xs space-y-6">
      {/* Countdown timer - always visible when token is active */}
      {hasActiveToken && !tokenFallbackUsed && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-muted/80 border border-border rounded-lg">
          <Clock className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-foreground/80">
            Link ready · expires in{' '}
            <span className="text-amber-400 font-mono font-semibold">
              {formatCountdown(countdown)}
            </span>
          </span>
        </div>
      )}

      {/* Primary action: Copy Link */}
      <button
        onClick={onCopyLink}
        disabled={isLoading}
        className={`w-full flex items-center justify-center gap-3 font-semibold py-4 px-6 rounded-xl transition-all ${
          copied
            ? 'bg-green-500 text-white'
            : 'bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 disabled:cursor-wait text-slate-900'
        }`}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Preparing Link...
          </>
        ) : copied ? (
          <>
            <Check className="w-5 h-5" />
            Copied! Now paste in Safari or Chrome
          </>
        ) : (
          <>
            <Copy className="w-5 h-5" />
            Copy Link
          </>
        )}
      </button>

      {/* Success message after copy */}
      {copied && hasActiveToken && (
        <p className="text-center text-green-400 text-sm font-medium">
          ✓ Link copied! Open Safari or Chrome and paste it there
        </p>
      )}

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-border" />
        <span className="text-muted-foreground text-sm">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Secondary option: Use menu */}
      <div className="bg-muted/50 border border-border rounded-xl p-5">
        <h3 className="text-foreground font-medium text-center mb-4">
          Open in browser directly
        </h3>

        {/* Step by step instructions */}
        <ol className="space-y-4">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-sm flex items-center justify-center font-medium">
              1
            </span>
            <div className="flex-1 pt-0.5">
              <p className="text-muted-foreground text-sm">
                Look for the{' '}
                <span className="text-foreground font-medium">•••</span> menu
                (usually at the top or bottom of the screen)
              </p>
            </div>
          </li>

          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-sm flex items-center justify-center font-medium">
              2
            </span>
            <div className="flex-1 pt-0.5">
              <p className="text-muted-foreground text-sm">
                Select{' '}
                <span className="text-foreground font-medium">"Open in Safari"</span>
                ,{' '}
                <span className="text-foreground font-medium">"Open in Browser"</span>
                , or{' '}
                <span className="text-foreground font-medium">
                  "Open in external browser"
                </span>
              </p>
            </div>
          </li>
        </ol>

        {/* Visual guide */}
        <div className="mt-5 relative">
          <div className="bg-muted/50 rounded-lg p-4 flex flex-col items-center">
            {/* Simplified phone mockup */}
            <div className="w-32 h-48 border-2 border-border rounded-2xl relative bg-muted">
              {/* Screen content placeholder */}
              <div className="absolute inset-2 bg-muted/80 rounded-xl" />

              {/* Bottom bar with menu indicator */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <div className="flex items-center gap-1 bg-muted px-3 py-1.5 rounded-full">
                  <span className="text-amber-400 text-xs font-bold animate-pulse">
                    •••
                  </span>
                </div>
              </div>

              {/* Arrow pointing to menu */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-amber-400 animate-bounce"
                >
                  <path
                    d="M12 5L12 19M12 19L5 12M12 19L19 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    transform="rotate(180 12 12)"
                  />
                </svg>
              </div>
            </div>

            <p className="text-muted-foreground text-xs mt-8">
              Look for the menu button
            </p>
          </div>
        </div>
      </div>

      {/* Login reminder - contextual based on token */}
      <p className="text-center text-muted-foreground text-xs">
        {hasActiveToken && !tokenFallbackUsed
          ? 'You\'ll stay logged in when you open the link'
          : 'You may need to log in again in Safari or Chrome'}
      </p>
    </div>
  );
}
